/// <amd-module name="Build/Admin/Common/Components/ElasticPoolSettings" />
import * as React from "react";

import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Icon } from "VSSUI/Icon";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";

import { OperatingSystemType, ResourceLimit } from "DistributedTask/Client/Contracts/TaskAgent";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";

import { FwLinks } from "Build/Admin/Common/Constants";

import * as Resources from "Build/Admin/Common/Resources";
import { ReimageDisabledWarning, reimageIsNotAvaialable } from "Build/Admin/Common/Components/ReimageDisabledWarning";


export interface IElasticPoolSettingsProps {
    showSaveFailedAgentCheckbox: boolean;
    recycleAfterEachUse: boolean;
    saveFailedAgents: boolean;
    agentInteractiveUI: boolean;
    maxCapacity: number | undefined;
    desiredIdle: number | undefined;
    timeToLiveMinutes: number | undefined;
    operatingSystem: OperatingSystemType | undefined;
    resourceLimits: ResourceLimit[];
    virtualMachineScaleSet: VirtualMachineScaleSet | undefined;
    onSettingsChanged(recycleAfterEachUse: boolean, saveFailedAgents: boolean, maxCapacity: number | undefined, desiredIdle: number | undefined, timeToLiveMinutes: number | undefined, agentInteractiveUI: boolean): void;
}

export class ElasticPoolSettings extends VssComponent<IElasticPoolSettingsProps> {

    private _disableReimageIfNoEphemeral: boolean;

    constructor(props: IElasticPoolSettingsProps, context: IVssComponentContext) {
        super(props, context);

        this._disableReimageIfNoEphemeral = isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.ElasticPoolDisableRecycleForNonEphemeral");
    }

    public render(): JSX.Element {
        const idleMoreThanMax: boolean = this.props.maxCapacity !== undefined && this.props.desiredIdle !== undefined && (this.props.desiredIdle < 0 || this.props.desiredIdle > this.props.maxCapacity);
        const invalidMaxSize: boolean = this.props.maxCapacity !== undefined && (this.props.maxCapacity < 0 || this.props.maxCapacity > 1000);
        const invalidTimeToLive: boolean = this.props.timeToLiveMinutes !== undefined && (this.props.timeToLiveMinutes < 0 || this.props.timeToLiveMinutes > 10000);
        const reimageDisabled: boolean = this._disableReimageIfNoEphemeral && reimageIsNotAvaialable(this.props.virtualMachineScaleSet);
        return (
            <div className="elastic-pool-settings flex-column">
                <Checkbox
                    className={css("recycle-checkbox", reimageDisabled && "disabled")}
                    checked={this.props.recycleAfterEachUse}
                    onChange={(ev, recycleAfterEachUse: boolean) =>
                        !reimageDisabled && this.props.onSettingsChanged(recycleAfterEachUse, this.props.saveFailedAgents, this.props.maxCapacity, this.props.desiredIdle, this.props.timeToLiveMinutes, this.props.agentInteractiveUI)}
                    label={Resources.RecycleAfterEachUse} />
                <ReimageDisabledWarning
                    isVisible={reimageDisabled}
                />
                <ConditionalChildren renderChildren={this.props.showSaveFailedAgentCheckbox}>
                    <Checkbox
                        className="save-failed-agents-checkbox"
                        checked={this.props.saveFailedAgents}
                        onChange={(ev, saveFailedAgents: boolean) =>
                            this.props.onSettingsChanged(this.props.recycleAfterEachUse, saveFailedAgents, this.props.maxCapacity, this.props.desiredIdle, this.props.timeToLiveMinutes, this.props.agentInteractiveUI)}
                        label={this._getSaveFailedAgentsLabel()} />
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.props.operatingSystem === OperatingSystemType.Windows}>
                    <Checkbox
                        className="agentInteractiveUI-checkbox"
                        checked={this.props.agentInteractiveUI}
                        onChange={(ev, agentInteractiveUI: boolean) =>
                            this.props.onSettingsChanged(this.props.recycleAfterEachUse, this.props.saveFailedAgents, this.props.maxCapacity, this.props.desiredIdle, this.props.timeToLiveMinutes, agentInteractiveUI)}
                        label={Resources.AgentInteractiveUI} />
                </ConditionalChildren>
                <div className="pool-setting-label">
                    {Resources.MaxCapacityLabel}
                </div>
                <TextField
                    className={css("max-capacity-input", invalidMaxSize ? "bolt-textfield-error" : undefined)}
                    value={this.props.maxCapacity !== undefined ? this.props.maxCapacity.toString() : undefined}
                    onChange={(ev, maxCapacity: string) =>
                        this.props.onSettingsChanged(this.props.recycleAfterEachUse, this.props.saveFailedAgents, this._parseSettingStringToNumber(maxCapacity), this.props.desiredIdle, this.props.timeToLiveMinutes, this.props.agentInteractiveUI)} />
                <ConditionalChildren renderChildren={invalidMaxSize}>
                    <div className="bolt-formitem-message bolt-formitem-message-error body-xs">{Resources.ElasticPoolInvalidMaxSizeError}</div>
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.props.resourceLimits !== undefined}>
                    {this._getResourceLimitsString()}
                </ConditionalChildren>
                <div className="pool-setting-label">
                    {Resources.DesiredIdleLabel}
                </div>
                <TextField
                    className={css("desired-idle-input", idleMoreThanMax ? "bolt-textfield-error" : undefined)}
                    value={this.props.desiredIdle !== undefined ? this.props.desiredIdle.toString() : undefined}
                    onChange={(ev, desiredIdle: string) =>
                        this.props.onSettingsChanged(this.props.recycleAfterEachUse, this.props.saveFailedAgents, this.props.maxCapacity, this._parseSettingStringToNumber(desiredIdle), this.props.timeToLiveMinutes, this.props.agentInteractiveUI)} />
                <ConditionalChildren renderChildren={idleMoreThanMax}>
                    <div className="bolt-formitem-message bolt-formitem-message-error body-xs">{Resources.ElasticPoolIdleMoreThanMaxError}</div>
                </ConditionalChildren>
                <div className="pool-setting-label">
                    {Resources.TimeToLiveMinutes}
                </div>
                <TextField
                    className={css("time-to-live-input", invalidTimeToLive ? "bolt-textfield-error" : undefined)}
                    value={this.props.timeToLiveMinutes !== undefined ? this.props.timeToLiveMinutes.toString() : undefined}
                    onChange={(ev, timeToLiveMinutes: string) =>
                        this.props.onSettingsChanged(this.props.recycleAfterEachUse, this.props.saveFailedAgents, this.props.maxCapacity, this.props.desiredIdle, this._parseSettingStringToNumber(timeToLiveMinutes), this.props.agentInteractiveUI)} />
                <ConditionalChildren renderChildren={invalidTimeToLive}>
                    <div className="bolt-formitem-message bolt-formitem-message-error body-xs">{Resources.ElasticPoolInvalidTimeToLiveError}</div>
                </ConditionalChildren>
            </div>
        );
    }

    private _getSaveFailedAgentsLabel(): JSX.Element {
        return (
            <span>
                {Resources.SaveFailedAgents}
                <Button
                    ariaLabel={Resources.SaveFailedAgentsLearnMore}
                    className="no-padding save-failed-agents-help-link"
                    subtle={true}
                    iconProps={{ iconName: "Info" }}
                    href={FwLinks.ElasticPoolTroubleshooting}
                    target="_blank" />
            </span>
        );
    }

    private _getResourceLimitsString(): JSX.Element | undefined {
        let privateSelfHostedJobCount: string | undefined = this._getSelfHostedParallelJobCount("Private");
        let publicSelfHostedJobCount: string | undefined = this._getSelfHostedParallelJobCount("Public");

        if (privateSelfHostedJobCount !== undefined) {
            // Default public count to unlimited
            if (publicSelfHostedJobCount === undefined) {
                publicSelfHostedJobCount = Resources.UnlimitedText;
            }

            return (
                <span className="bolt-formitem-message body-xs">
                    {format(Resources.SelfHostedParallelJobs, privateSelfHostedJobCount, publicSelfHostedJobCount)}
                    <Link className="margin-left-4" href={FwLinks.ParallelJobsDocs} target="_blank">{Resources.LearnMore}</Link>
                </span>
            );
        }
        else {
            return undefined;
        }
    }

    private _getSelfHostedParallelJobCount(parallelismTag: string): string | undefined {
        if (this.props.resourceLimits !== undefined) {
            const selfHostedLimit: ResourceLimit | undefined = this.props.resourceLimits.find(
                r => (!r.isHosted && localeIgnoreCaseComparer(r.parallelismTag, parallelismTag) === 0)
            );
            if (selfHostedLimit?.totalCount !== undefined) {
                if (selfHostedLimit.totalCount >= 10000) {
                    return Resources.UnlimitedText;
                }
                else {
                    return selfHostedLimit.totalCount.toString();
                }
            }
        }

        return undefined;
    }

    private _parseSettingStringToNumber(setting: string): number | undefined {
        const settingNumber: number = parseInt(setting, 10);
        if (isNaN(settingNumber)) {
            return undefined;
        }
        else {
            return settingNumber;
        }
    }
}
